
import { Component, Prop, Vue } from 'vue-property-decorator';
import { SelectUserInterface, UserBaseInfoInterface, UserRepoInterface } from '@/services/Users/Interfaces';
import { WPXFormValue } from '@/components/templates/WPXForm';
import { EscalateForm } from '@/services/QA/escalation/forms/EscalateForm';
import { EscalationPriority, EscalationStatus } from '@/services/QA/escalation';
import {
  CreateEscalationInterface,
  EscalationInterface,
  EscalationPriorityInterface,
  EscalationRepositoryInterface,
} from '@/services/QA/escalation/interfaces';
import { ProfileRepositoryInterface } from '@/services/Users-profile/interfaces';

@Component({
  inject: [
    'userRepository',
    'escalationRepository',
    'profileRepository',
  ],
})
export default class EscalateDialog extends Vue {
  @Prop() public chatId: string;
  @Prop() public ticketId: number;
  @Prop() public agentsIds: string[];
  @Prop() public agents: string[];
  @Prop() public updateEscalation!: EscalationInterface;

  private departmentManagers: SelectUserInterface[];
  private shiftManagers: SelectUserInterface[];
  private userRepository!: UserRepoInterface;
  private escalationRepository!: EscalationRepositoryInterface;
  private profileRepository!: ProfileRepositoryInterface;
  private form: WPXFormValue<EscalateForm> = new WPXFormValue(EscalateForm);
  private priorities: EscalationPriorityInterface[] = [];
  private existingEscalationId!: number;

  private visible: boolean = true;
  private loading: boolean = true;

  public async created(): Promise<void> {
    this.shiftManagers = [];
    this.departmentManagers = [];
    await this.fetchUsersData();

    Object.keys(EscalationPriority).forEach((key, index) => {
      this.priorities.push({ key, value: Object.values(EscalationPriority)[index] });
    });

    if (this.updateEscalation) {
      const existingEscalation = await this.escalationRepository.getEscalationByResourceId(this.chatId);
      if (existingEscalation) {
        this.form.data.departmentManager = existingEscalation.departmentManager;
        this.form.data.reason = existingEscalation.reason;
        this.form.data.priority = existingEscalation.priority as EscalationPriority;
        this.existingEscalationId = existingEscalation.id;
      }
    }
    this.loading = false;
  }

  private async fetchUsersData(): Promise<void> {
    const departmentManagers = await this.userRepository.getQaManagers();
    const shiftManagers = await this.userRepository.getShiftManagers();

    const profiles = await this.profileRepository.getBaseProfileDataByUsersIds([
      ...departmentManagers.map((depManager) => String(depManager.id)),
      ...shiftManagers.map((shiftManager) => String(shiftManager.id)),
    ]);

    profiles.forEach((profile) => {
      const deptManagerIndex = departmentManagers.findIndex((deptManager) => deptManager.id === profile.userId);
      if (deptManagerIndex !== -1) {
        this.departmentManagers.push({
          id: profile.userId,
          name: `${profile.firstName} ${profile.lastName}`,
        });
      }

      const shiftManagerIndex = shiftManagers.findIndex((shiftManager) => shiftManager.id === profile.userId);
      if (shiftManagerIndex !== -1) {
        this.shiftManagers.push({
          id: profile.userId,
          name: `${profile.firstName} ${profile.lastName}`,
        });
      }
    });
  }

  private async escalate(): Promise<void> {
    try {
      const escalationData: CreateEscalationInterface = {
        departmentManager: this.form.data.departmentManager,
        shiftManager: this.form.data.shiftManager,
        escalatedFromUserId: this.$store.getters.id,
        timestamp: Math.floor(Date.now() / 1000),
        priority: this.form.data.priority,
        reason: this.form.data.reason,
        status: EscalationStatus.OPEN,
      };
      if (this.updateEscalation) {
        await this.escalationRepository.updateEscalation(this.existingEscalationId, escalationData);
      } else {
        if (this.chatId) {
          await this.escalationRepository.createChatEscalation(this.chatId, this.agentsIds, escalationData);
        }
        if (this.ticketId) {
          await this.escalationRepository.createTicketEscalation(this.ticketId, this.agentsIds, escalationData);
        }
      }
      this.$successDialog.show(`${this.chatId ? 'Chat' : 'Ticket'} Escalated Successfully`);
      this.destroyComponent();
      this.$emit('escalated');
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message);
    }
  }

  private destroyComponent(): void {
    this.$emit('close');
  }

  private validateForm(): void {
    const shiftManagerSelected = !!this.form.data.shiftManager.length;
    const departmentManagerSelected = !!this.form.data.departmentManager.length;
    const hasReason = !!this.form.data.reason.length;
    const hasPriority = !!this.form.data.priority.length;

    if ((shiftManagerSelected || departmentManagerSelected) && hasReason && hasPriority) {
      this.form.valid = true;
    }
  }
}
